import {Button, Checkbox, Dialog, InputGroup, Label, MenuItem, Text, Tooltip} from "@blueprintjs/core";
import log from "loglevel";
import {Select} from "@blueprintjs/select";
import styled from "@emotion/styled";
import {light} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {format, getDate, getHours, getMinutes, getMonth, getYear, parse, startOfMinute} from "date-fns";
import {isEmpty} from "lodash";
import {ChangeEvent, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Controller, ControllerRenderProps, SubmitErrorHandler, SubmitHandler, Validate, useForm} from "react-hook-form";
import {useAppContext} from "../../../../ApplicationContext";
import {
  EventType,
  EventTypeSortableField,
  LateStop,
  LateStopSortableField,
  ManifestStop,
  SaveClearJobStop,
  SearchableSortDirection,
  Service,
  ServiceSortableField,
  useSaveClearJobStopsMutation,
  useSearchEventTypesQuery,
  useSearchLateStopsQuery,
  useSearchServicesQuery
} from "../../../../generated/graphql";
import {FeatureFlagContext} from "../../../../providers/FeatureFlagProvider";
import {PreferenceContext, extractSimplePref} from "../../../../providers/PreferenceProvider";
import {formatCMSDateTimeInTimeZone, isValidDateTime} from "../../../../utils/DateTimeUtils";
import {
  Constants,
  DateFormats,
  SaveAndClearJobStopDateTimeSource,
  TenantPreferences
} from "../../../../constants/constants";
import {InforIcon} from "../../../job/components/AssignmentBarV2";
import CompleteStopIdexxRules from "./CompleteStopIdexxRules";
import TimePicker from "../../../common/date-time/TimePicker";
import DateInput from "../../../common/date-time/DateInput";
import {TManifestEnhanced, TManifestStopEnhanced} from "../../../common/types";

type CompleteStopDialogProps = {
  manifestStop: TManifestStopEnhanced;
  manifest: TManifestEnhanced;
  onCancel?: () => void;
  markComplete?: (success: boolean) => void;
};

type DateTimeValidator =
  | Validate<string | undefined, IFormValues>
  | Record<string, Validate<string | undefined, IFormValues>>
  | undefined;

enum TriggerType {
  StopComplete = 0,
  ArriveLocation = 1,
  StopSelected = 2
}

enum TimeType {
  ScheduledTime = 0,
  LateTime = 1
}

type IFormValues = {
  podName: string | undefined;
  podDateTime: string;
  arrivalDateTime: string | undefined;
  departureDateTime: string | undefined;
  pieces: string;
  weight: string;
  lateReason?: string;
};

const TimeInforIcon = () => (
  <Tooltip
    position="top"
    content="Enter this time based on the time in your time zone, not the driver's time zone."
    popoverClassName="white-popover mark-complete-stop-time-popover"
  >
    <InforIcon size={12} />
  </Tooltip>
);

const getDateTime = (date: number | Date, podTime: Date | undefined) =>
  new Date(
    getYear(date),
    getMonth(date),
    getDate(date),
    podTime ? getHours(podTime) : 0,
    podTime ? getMinutes(podTime) : 0,
    0,
    0
  );

export enum DateTimeErrorMsg {
  FutureTimeError = "The time entered must be equal to the current time, or in the past",
  InvalidDate = "Invalid date",
  PodPriorArrTime = "The POD time must be at least 1 minute after the arrival time",
  DepTimePriorArrTime = "The departure time must be at least 1 minute after the arrival time",
  PODAfterScheduledTime = "The POD time can't be entered prior to the scheduled time of stop #2 of the IDEXX job",
  ArrivalAfterScheduledTime = "The arrival time can't be entered prior to the scheduled time of stop #2 of the IDEXX job",
  DepartureAfterScheduledTime = "The departure time can't be entered prior to the scheduled time of stop #2 of the IDEXX job",
  PodOverlapsTimeWindowOfAnotherStopInSameJob = "The POD time entered overlaps with another arrival/departure time window established for this IDEXX job",
  ArrivalOverlapsTimeWindowOfAnotherStopInSameJob = "The arrival time entered overlaps with another arrival/departure time window established for this IDEXX job",
  DepartureOverlapsTimeWindowOfAnotherStopInSameJob = "The departure time entered overlaps with another arrival/departure time window established for this IDEXX job"
}

const titleMessage = "Additional Information to mark stop(s) as complete";

const CompleteStopDialog = ({manifestStop, manifest, onCancel, markComplete}: CompleteStopDialogProps) => {
  const {tenantPreferences} = useContext(PreferenceContext);
  const requireArrAndDepTimes = extractSimplePref(tenantPreferences, TenantPreferences.requireArrAndDepTimes, false)
    .value as boolean;
  const idexxCompanySettingEnabled = extractSimplePref(
    tenantPreferences,
    TenantPreferences.idexxCompleteStopLogic,
    false
  ).value as boolean;

  const allowStopCompletedAtFutureTime = extractSimplePref(
    tenantPreferences,
    TenantPreferences.stopCompletedAtFutureTime,
    false
  ).value as boolean;

  const {appState} = useAppContext();
  const {isTimeFormat24hr} = appState;

  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [lateStops, setLateStops] = useState<LateStop[]>([]);
  const [services, setServices] = useState<Service[]>([]);

  const [markCompleteJob] = useSaveClearJobStopsMutation();
  const {idexxLogicCompleteStops: idexxFeatureEnabled} = useContext(FeatureFlagContext);

  const idexxRegEx = "^(idexx).*";

  useSearchEventTypesQuery({
    variables: {
      filter: {
        lateReason: {
          eq: true
        }
      },
      sort: {
        field: EventTypeSortableField.EventType,
        direction: SearchableSortDirection.Asc
      },
      limit: 20
    },
    onCompleted(data) {
      if (data) {
        setEventTypes([
          {
            eventType: "(none)",
            eventTypeId: 0
          },
          ...(data.searchEventTypes?.items as EventType[])
        ]);
      }
    },
    onError: log.error
  });

  useSearchLateStopsQuery({
    variables: {
      sort: {
        field: LateStopSortableField.Sequence,
        direction: SearchableSortDirection.Asc
      }
    },
    onCompleted(data) {
      if (data) {
        setLateStops(data?.searchLateStops?.items as LateStop[]);
      }
    },
    onError: log.error
  });

  useSearchServicesQuery({
    variables: {
      sort: {
        field: ServiceSortableField.ServiceId,
        direction: SearchableSortDirection.Asc
      }
    },
    onCompleted(data) {
      if (data) {
        setServices(data?.searchServices?.items as Service[]);
      }
    },
    onError: log.error
  });

  const currentDateTime = useMemo(() => new Date(), []);

  const {orderPodRequired, piecesOnTabExists, stopPodRequired} = useMemo(
    () => ({
      piecesOnTabExists: (manifestStop.order?.tabPieces ?? 0) > 0,
      stopPodRequired: manifestStop.podRequired ?? false,
      orderPodRequired: manifestStop.order?.podRequired ?? false
    }),
    [manifestStop.order?.podRequired, manifestStop.order?.tabPieces, manifestStop.podRequired]
  );

  const podRequired = useMemo<boolean>(() => {
    if (stopPodRequired) return true;
    if (orderPodRequired && manifestStop.stopType != "P") return true;
    return false;
  }, [manifestStop.stopType, orderPodRequired, stopPodRequired]);

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    trigger,
    formState: {errors, isValid, validatingFields, touchedFields}
  } = useForm<IFormValues>({
    mode: "onBlur",
    defaultValues: {
      lateReason: "",
      podDateTime: CompleteStopIdexxRules.isIdexxJob(manifestStop, idexxRegEx)
        ? undefined
        : currentDateTime.toISOString(),
      arrivalDateTime: manifestStop.arriveDateTime ?? undefined,
      weight: manifestStop.job?.weight?.toString() ?? "0",
      pieces: manifestStop.job?.pieces?.toString() ?? "0"
    }
  });

  const [podDateTime, arrivalDateTime, departureDateTime] = [
    watch("podDateTime"),
    watch("arrivalDateTime"),
    watch("departureDateTime")
  ];

  const failedSubmitHandler: SubmitErrorHandler<IFormValues> | undefined = useCallback((values) => {
    log.error("Failed form validation on complete stop submit.", values);
  }, []);

  const submitHandler: SubmitHandler<IFormValues> = useCallback(
    async (data) => {
      const payload: SaveClearJobStop = {
        jobStopId: manifestStop.jobStopId,
        podName: data.podName,
        podDateTime: formatCMSDateTimeInTimeZone(
          data.podDateTime,
          manifestStop.timeZone as string,
          DateFormats.awsDateTimeWithOffset
        ),
        eventType: data.lateReason,
        arriveDateTime: arrivalDateTime
          ? formatCMSDateTimeInTimeZone(
              arrivalDateTime,
              manifestStop.timeZone as string,
              DateFormats.awsDateTimeWithOffset
            )
          : undefined,
        departDateTime: departureDateTime
          ? formatCMSDateTimeInTimeZone(
              departureDateTime,
              manifestStop.timeZone as string,
              DateFormats.awsDateTimeWithOffset
            )
          : undefined,
        pieces: Number(data.pieces),
        weight: Number(data.weight),
        arriveDateTimeSource: SaveAndClearJobStopDateTimeSource.USER,
        departDateTimeSource: SaveAndClearJobStopDateTimeSource.USER
      };

      try {
        await markCompleteJob({
          variables: {
            saveClearJobStops: payload
          }
        });
        markComplete?.(true);
      } catch (err) {
        log.error("Failed to save clear job stop", err);
        markComplete?.(false);
      }
    },
    [arrivalDateTime, departureDateTime, manifestStop.jobStopId, manifestStop.timeZone, markComplete, markCompleteJob]
  );

  const isLate = useCallback(() => {
    const siteValid = (lateStopSiteId: number) => {
      const siteId = manifestStop.job.site?.siteId;
      if (lateStopSiteId === 0 || lateStopSiteId === siteId) return true;
      else return false;
    };

    const customerValid = (lateStopCustomerId: number) => {
      const customerId = manifestStop.order.customer.customerId;
      if (lateStopCustomerId === 0 || lateStopCustomerId === customerId) return true;
      else return false;
    };

    const customerGroupValid = (lateStopCustomerGroup: string) => {
      const customerGroup = manifestStop.order.customer.group;
      if (!lateStopCustomerGroup || lateStopCustomerGroup === customerGroup) return true;
      else return false;
    };

    const serviceValid = (lateStopServiceId: number) => {
      const lateStopServiceName = services.find((s) => s.serviceId === lateStopServiceId)?.service;
      if (!lateStopServiceName || lateStopServiceName === manifestStop.order.service) return true;
      else return false;
    };

    const stopTypeValid = (lateStopType: string) => {
      return lateStopType === manifestStop.stopType;
    };

    const timeTypeValid = (lateStopTimeType: TimeType) => {
      if (lateStopTimeType === TimeType.ScheduledTime) {
        const scheduledDateTime = Date.parse(manifestStop?.scheduledDateTime as string);
        if (isNaN(scheduledDateTime)) {
          log.debug("[CompleteStopDialog] - Time type is scheduled time but scheduledDateTime is not valid");
          return false;
        }
      } else if (lateStopTimeType === TimeType.LateTime) {
        const lateDateTime = Date.parse(manifestStop?.lateDateTime as string);
        if (isNaN(lateDateTime)) {
          log.debug("[CompleteStopDialog] - Time type is late time but lateDateTime is not valid");
          return false;
        }
      }
      return lateStopTimeType === TimeType.ScheduledTime || lateStopTimeType === TimeType.LateTime;
    };

    for (const lateStop of lateStops) {
      if (
        siteValid(lateStop.siteId as number) &&
        customerValid(lateStop.customerId as number) &&
        customerGroupValid(lateStop.customerGroup as string) &&
        serviceValid(lateStop.serviceId as number) &&
        stopTypeValid(lateStop.stopType as string) &&
        timeTypeValid(lateStop.timeType)
      ) {
        const compareDateTime = Date.parse(podDateTime);
        const diff =
          lateStop.timeType === TimeType.ScheduledTime
            ? compareDateTime - Date.parse(manifestStop?.scheduledDateTime as string)
            : compareDateTime - Date.parse(manifestStop?.lateDateTime as string);
        const late = diff >= Number(lateStop.stopMinutes) * 60 * 1000;
        return late;
      }
    }
    return false;
  }, [
    lateStops,
    manifestStop.job.site?.siteId,
    manifestStop?.lateDateTime,
    manifestStop.order.customer.customerId,
    manifestStop.order.customer.group,
    manifestStop.order.service,
    manifestStop?.scheduledDateTime,
    manifestStop.stopType,
    podDateTime,
    services
  ]);

  const setValueAndValidate = useCallback(
    (fieldName: any, fieldValue: string) => {
      setValue(fieldName, fieldValue, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue]
  );

  const lateReasonDisabled = useCallback(() => {
    if (podDateTime && !isNaN(Date.parse(podDateTime))) {
      const late = isLate();
      if (late && getValues().lateReason === undefined) {
        setValueAndValidate("lateReason", "");
      }
      return !late;
    } else {
      return true;
    }
  }, [getValues, isLate, podDateTime, setValueAndValidate]);

  const eventTypeItemRenderer = (item: EventType) => {
    return (
      <MenuItem
        text={item.eventType}
        selected={getValues()?.lateReason === item.eventType}
        onClick={() => {
          log.debug("lateItemClicked " + item.eventType);
          setValueAndValidate("lateReason", item.eventType as string);
        }}
      />
    );
  };

  const handleTimeChange = useCallback(
    (value: unknown, field: ControllerRenderProps<IFormValues, any>) => {
      const fieldDateTime = field.value ? new Date(field.value) : new Date();
      if (isValidDateTime(fieldDateTime) && isValidDateTime(value)) {
        const time = value as Date;
        setValueAndValidate(field.name, getDateTime(fieldDateTime, time).toISOString());
      }
    },
    [setValueAndValidate]
  );

  const handleDateChange = useCallback(
    (newDate: string | null, field: ControllerRenderProps<IFormValues, any>) => {
      if (newDate !== null) {
        const fieldDateTime = field.value ? new Date(field.value) : new Date();
        if (isValidDateTime(fieldDateTime)) {
          const date = parse(newDate, "yyyy-MM-dd", new Date());
          setValueAndValidate(field.name, getDateTime(date, fieldDateTime).toISOString());
        }
      }
    },
    [setValueAndValidate]
  );

  const handleCurrentPOD = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setValueAndValidate("podDateTime", new Date().toISOString());
      }
    },
    [setValueAndValidate]
  );

  const handleCurrentArrival = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setValueAndValidate("arrivalDateTime", new Date().toISOString());
      }
    },
    [setValueAndValidate]
  );

  const handleCurrentDeparture = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setValueAndValidate("departureDateTime", new Date().toISOString());
      }
    },
    [setValueAndValidate]
  );

  const isNotFutureDate = useCallback(
    (dateToCheck: string | undefined) => {
      if (dateToCheck === undefined || allowStopCompletedAtFutureTime) return true;
      return new Date(dateToCheck) <= new Date() || DateTimeErrorMsg.FutureTimeError;
    },
    [allowStopCompletedAtFutureTime]
  );

  const isValidDate = useCallback((dateToCheck: string | undefined) => {
    if (dateToCheck === undefined) return true;
    return isValidDateTime(dateToCheck) || DateTimeErrorMsg.InvalidDate;
  }, []);

  const isPodDateTimeAfterArrivalDateTime = useCallback(
    (podDateTime: string | undefined, arrivalDateTime: string | undefined) => {
      if (arrivalDateTime && podDateTime) {
        if (
          +startOfMinute(new Date(podDateTime)) - +startOfMinute(new Date(arrivalDateTime)) <
          Constants.ARRIVAL_TIME_PRIOR_AT_LEAST
        ) {
          return DateTimeErrorMsg.PodPriorArrTime;
        }
      }
      return true;
    },
    []
  );

  const isDepartureDateTimeAfterArrivalDateTime = useCallback(
    (departureDateTime: string | undefined, arrivalDateTime: string | undefined) => {
      if (departureDateTime && arrivalDateTime) {
        if (
          +startOfMinute(new Date(departureDateTime)) - +startOfMinute(new Date(arrivalDateTime)) <
          Constants.ARRIVAL_TIME_PRIOR_AT_LEAST
        ) {
          return DateTimeErrorMsg.DepTimePriorArrTime;
        }
      }
      return true;
    },
    []
  );

  const isPodDateTimeAfterScheduledTime = useCallback(
    (podDateTime: string | undefined) => {
      return CompleteStopIdexxRules.isPodDateTimeAfterScheduledTimeOfStop2(
        podDateTime,
        manifest.stops,
        manifestStop.job.jobId
      );
    },
    [manifest.stops, manifestStop.job.jobId]
  );

  const isArrivalDateTimeAfterScheduledTime = useCallback(
    (arrivalDateTime: string | undefined) => {
      return CompleteStopIdexxRules.isArrivalDateTimeAfterScheduledTimeOfStop2(
        arrivalDateTime,
        manifest.stops,
        manifestStop.job.jobId
      );
    },
    [manifest.stops, manifestStop.job.jobId]
  );

  const isDepartureDateTimeAfterScheduledTime = useCallback(
    (departureDateTime: string | undefined) => {
      return CompleteStopIdexxRules.isDepartureDateTimeAfterScheduledTimeOfStop2(
        departureDateTime,
        manifest.stops,
        manifestStop.job.jobId
      );
    },
    [manifest.stops, manifestStop.job.jobId]
  );

  const isPodDateTimeOutsideTimeWindowOfOtherStopsInSameJob = useCallback(
    (podDateTime: string | undefined, stops: ManifestStop[]) => {
      return CompleteStopIdexxRules.isPodDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
        podDateTime,
        stops,
        manifestStop.job.jobId
      );
    },
    [manifestStop.job.jobId]
  );

  const isArrivalDateTimeOutsideTimeWindowOfOtherStopsInSameJob = useCallback(
    (arrivalDateTime: string | undefined, stops: ManifestStop[]) => {
      return CompleteStopIdexxRules.isArrivalDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
        arrivalDateTime,
        stops,
        manifestStop.job.jobId
      );
    },
    [manifestStop.job.jobId]
  );

  const isDepartureDateTimeOutsideTimeWindowOfOtherStopsInSameJob = useCallback(
    (departureDateTime: string | undefined, stops: ManifestStop[]) => {
      return CompleteStopIdexxRules.isDepartureDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
        departureDateTime,
        stops,
        manifestStop.job.jobId
      );
    },
    [manifestStop.job.jobId]
  );

  const podDateTimeValidator = useMemo<DateTimeValidator>(() => {
    return {
      isNotFuture: (v) => isNotFutureDate(v),
      isValid: (v) => isValidDate(v),
      isPodAfterArrivalTime: (v) => isPodDateTimeAfterArrivalDateTime(v, arrivalDateTime),
      isPodAfterScheduledTime: function (v) {
        if (idexxFeatureEnabled && idexxCompanySettingEnabled && v) {
          if (CompleteStopIdexxRules.isIdexxJob(manifestStop, idexxRegEx)) {
            if (
              !CompleteStopIdexxRules.isFirstIdexxStopInJob(
                manifest.stops ? manifest.stops : [],
                manifestStop.jobStopId,
                manifestStop.job.jobId,
                idexxRegEx
              )
            ) {
              return isPodDateTimeAfterScheduledTime(v);
            }
          }
        }
        return true;
      },
      isPodDateTimeOutsideTimeWindowOfAnotherStopInSameJob: function (v) {
        if (idexxFeatureEnabled && idexxCompanySettingEnabled && v) {
          if (CompleteStopIdexxRules.isIdexxJob(manifestStop, idexxRegEx)) {
            if (
              !CompleteStopIdexxRules.isFirstIdexxStopInJob(
                manifest.stops ? manifest.stops : [],
                manifestStop.jobStopId,
                manifestStop.job.jobId,
                idexxRegEx
              )
            ) {
              return isPodDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
                v,
                manifest.stops ? manifest.stops?.filter((stop) => stop.job.jobId === manifestStop.job.jobId) : []
              );
            }
          }
        }
        return true;
      }
    };
  }, [
    isNotFutureDate,
    isValidDate,
    isPodDateTimeAfterArrivalDateTime,
    arrivalDateTime,
    idexxFeatureEnabled,
    idexxCompanySettingEnabled,
    idexxRegEx,
    manifestStop,
    isPodDateTimeAfterScheduledTime,
    isPodDateTimeOutsideTimeWindowOfOtherStopsInSameJob,
    manifest.stops
  ]);

  const arrivalDateTimeValidator = useMemo<DateTimeValidator>(() => {
    return {
      isNotFuture: (v) => isNotFutureDate(v),
      isValid: (v) => isValidDate(v),
      isPodAfterArrivalTime: (v) => isPodDateTimeAfterArrivalDateTime(podDateTime, v),
      isDepartureAfterArrivalTime: (v) => isDepartureDateTimeAfterArrivalDateTime(departureDateTime, v),
      isArrivalAfterScheduledTime: function (v) {
        if (idexxFeatureEnabled && idexxCompanySettingEnabled && v) {
          if (CompleteStopIdexxRules.isIdexxJob(manifestStop, idexxRegEx)) {
            if (
              !CompleteStopIdexxRules.isFirstIdexxStopInJob(
                manifest.stops ? manifest.stops : [],
                manifestStop.jobStopId,
                manifestStop.job.jobId,
                idexxRegEx
              )
            ) {
              return isArrivalDateTimeAfterScheduledTime(v);
            }
          }
        }
        return true;
      },
      isArrivalDateTimeOutsideTimeWindowOfAnotherStopInSameJob: function (v) {
        if (idexxFeatureEnabled && idexxCompanySettingEnabled && v) {
          if (CompleteStopIdexxRules.isIdexxJob(manifestStop, idexxRegEx)) {
            if (
              !CompleteStopIdexxRules.isFirstIdexxStopInJob(
                manifest.stops ? manifest.stops : [],
                manifestStop.jobStopId,
                manifestStop.job.jobId,
                idexxRegEx
              )
            ) {
              return isArrivalDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
                v,
                manifest.stops ? manifest.stops?.filter((stop) => stop.job.jobId === manifestStop.job.jobId) : []
              );
            }
          }
        }
        return true;
      }
    };
  }, [
    isNotFutureDate,
    isValidDate,
    isPodDateTimeAfterArrivalDateTime,
    podDateTime,
    isDepartureDateTimeAfterArrivalDateTime,
    departureDateTime,
    idexxFeatureEnabled,
    idexxCompanySettingEnabled,
    idexxRegEx,
    manifestStop,
    isArrivalDateTimeAfterScheduledTime,
    isArrivalDateTimeOutsideTimeWindowOfOtherStopsInSameJob,
    manifest.stops
  ]);

  const departureDateTimeValidator = useMemo<DateTimeValidator>(() => {
    return {
      isNotFuture: (v) => isNotFutureDate(v),
      isValid: (v) => isValidDate(v),
      isDepartureAfterArrivalTime: (v) => isDepartureDateTimeAfterArrivalDateTime(v, arrivalDateTime),
      isDepartureAfterScheduledTime: function (v) {
        if (idexxFeatureEnabled && idexxCompanySettingEnabled && v) {
          if (CompleteStopIdexxRules.isIdexxJob(manifestStop, idexxRegEx)) {
            if (
              !CompleteStopIdexxRules.isFirstIdexxStopInJob(
                manifest.stops ? manifest.stops : [],
                manifestStop.jobStopId,
                manifestStop.job.jobId,
                idexxRegEx
              )
            ) {
              return isDepartureDateTimeAfterScheduledTime(v);
            }
          }
        }
        return true;
      },
      isDepartureDateTimeOutsideTimeWindowOfOtherStopsInSameJob: function (v) {
        if (idexxFeatureEnabled && idexxCompanySettingEnabled && v) {
          if (CompleteStopIdexxRules.isIdexxJob(manifestStop, idexxRegEx)) {
            if (
              !CompleteStopIdexxRules.isFirstIdexxStopInJob(
                manifest.stops ? manifest.stops : [],
                manifestStop.jobStopId,
                manifestStop.job.jobId,
                idexxRegEx
              )
            ) {
              return isDepartureDateTimeOutsideTimeWindowOfOtherStopsInSameJob(
                v,
                manifest.stops ? manifest.stops?.filter((stop) => stop.job.jobId === manifestStop.job.jobId) : []
              );
            }
          }
        }
        return true;
      }
    };
  }, [
    arrivalDateTime,
    idexxCompanySettingEnabled,
    idexxFeatureEnabled,
    isDepartureDateTimeAfterArrivalDateTime,
    isDepartureDateTimeAfterScheduledTime,
    isDepartureDateTimeOutsideTimeWindowOfOtherStopsInSameJob,
    idexxRegEx,
    isNotFutureDate,
    isValidDate,
    manifest.stops,
    manifestStop
  ]);

  useEffect(() => {
    log.debug("Field validation warnings", {
      errors: errors,
      validatingFields: validatingFields,
      touchedFields: touchedFields,
      isValid: isValid
    });
  }, [errors, isValid, touchedFields, validatingFields]);

  useEffect(() => {
    const inputEl = document.querySelector("div.arrivalDate input") as HTMLInputElement;
    if (arrivalDateTime && inputEl && !inputEl.value) {
      inputEl.value = format(new Date(arrivalDateTime), "MM/dd/yyyy");
    }
  }, [arrivalDateTime]);

  useEffect(() => {
    const inputEl = document.querySelector("div.podDate input") as HTMLInputElement;
    if (podDateTime && inputEl && !inputEl.value) {
      inputEl.value = format(new Date(podDateTime), "MM/dd/yyyy");
    }
  }, [podDateTime]);

  useEffect(() => {
    const inputEl = document.querySelector("div.departureDate input") as HTMLInputElement;
    if (departureDateTime && inputEl && !inputEl.value) {
      inputEl.value = format(new Date(departureDateTime), "MM/dd/yyyy");
    }
  }, [departureDateTime]);

  // Revalidate all datetime fields that have values when any of them changes
  useEffect(() => {
    const fieldsToValidate: (keyof IFormValues)[] = [];
    if (podDateTime) fieldsToValidate.push("podDateTime");
    if (arrivalDateTime) fieldsToValidate.push("arrivalDateTime");
    if (departureDateTime) fieldsToValidate.push("departureDateTime");
    if (fieldsToValidate.length > 0) {
      trigger(fieldsToValidate);
    }
  }, [podDateTime, arrivalDateTime, departureDateTime, trigger]);

  const renderFormatStopDateTime = useMemo(() => {
    return `MM/dd/yyyy - ${isTimeFormat24hr ? "HH:mm" : "hh:mm a"}`;
  }, [isTimeFormat24hr]);

  return (
    <StyledDialog isOpen={true} title={titleMessage} isCloseButtonShown={false}>
      <DialogBody>
        <ManifestSequence>#{manifestStop.manifestSequence}</ManifestSequence>
        <HeaderHR />
        <InfoGroup>
          <InfoItem>
            <InfoTitleLeft>Expected delivery time</InfoTitleLeft>
            {manifestStop.earlyDateTime && (
              <InfoLineItemLeft title={new Date(Date.parse(manifestStop.earlyDateTime as string)).toLocaleDateString()}>
                Early time: {format(Date.parse(manifestStop.earlyDateTime), renderFormatStopDateTime)}
              </InfoLineItemLeft>
            )}
            {manifestStop.scheduledDateTime && (
              <InfoLineItemLeft
                title={new Date(Date.parse(manifestStop.scheduledDateTime as string)).toLocaleDateString()}
              >
                Scheduled time: {format(Date.parse(manifestStop.scheduledDateTime), renderFormatStopDateTime)}
              </InfoLineItemLeft>
            )}
            {manifestStop.lateDateTime && (
              <InfoLineItemLeft title={new Date(Date.parse(manifestStop.lateDateTime as string)).toLocaleDateString()}>
                Late time: {format(Date.parse(manifestStop.lateDateTime), renderFormatStopDateTime)}
              </InfoLineItemLeft>
            )}
          </InfoItem>
          <InfoItem>
            <InfoTitleRight>Stop location</InfoTitleRight>
            <InfoLineItemRight>{manifestStop.address}</InfoLineItemRight>
            <InfoLineItemRight>
              {manifestStop.city}, {manifestStop.state}
            </InfoLineItemRight>
            <InfoLineItemRight>{manifestStop.zip}</InfoLineItemRight>
          </InfoItem>
        </InfoGroup>
        <StyledForm onSubmit={handleSubmit(submitHandler, failedSubmitHandler) as any}>
          {/* POD Information */}
          <InfoTitleLeft>1. POD Information</InfoTitleLeft>
          <StyledLabel htmlFor="podName" isRequired={podRequired}>
            Name of the person who accepted delivery
          </StyledLabel>
          <Controller
            name="podName"
            control={control}
            rules={{
              required: podRequired,
              validate: {
                required: (v) => {
                  if (podRequired && v === "") {
                    return "POD name is required";
                  }
                  return true; //if not required, always return true to pass the required check
                }
              }
            }}
            render={({field}) => <InputGroup id="podName" placeholder="ex. Anna Smith" {...field} />}
          />
          <DateTimeGroup>
            <div>
              <StyledLabel htmlFor="podDate" isRequired={podRequired}>
                POD date
              </StyledLabel>
              <Controller
                name="podDateTime"
                control={control}
                rules={{
                  required: podRequired,
                  validate: podDateTimeValidator
                }}
                render={({field}) => (
                  <DateInput
                    isValid={!errors.podDateTime}
                    formatDate={(date: Date) => {
                      return format(date, "MM/dd/yyyy");
                    }}
                    parseDate={(str: string) => {
                      return parse(str, "MM/dd/yyyy", new Date());
                    }}
                    placeholder="ex. 01/01/2021"
                    onError={(error: Date) => {
                      if (error) {
                        control.setError("podDateTime", {
                          message: DateTimeErrorMsg.InvalidDate
                        });
                      }
                    }}
                    {...field}
                    onChange={(newDate: string | null) => handleDateChange(newDate, field)}
                    inputProps={{
                      id: "podDate"
                    }}
                    className="podDate"
                    rightElement={
                      <CalendarIcon
                        icon={light("calendar")}
                        size="sm"
                        onClick={() => {
                          const inputEl = document.querySelector("div.podDate input") as HTMLInputElement;
                          inputEl?.focus();
                        }}
                      />
                    }
                  />
                )}
              />
            </div>
            <div>
              <StyledLabel htmlFor="podTime" isRequired={podRequired}>
                POD time
                <TimeInforIcon />
              </StyledLabel>
              <Controller
                name="podDateTime"
                control={control}
                rules={{
                  required: podRequired,
                  validate: podDateTimeValidator
                }}
                render={({field}) => (
                  <TimePicker
                    isValid={!errors.podDateTime}
                    className="optimization-time-picker podTime"
                    {...field}
                    value={field.value ? new Date(field.value) : undefined}
                    onChange={(value: unknown) => handleTimeChange(value, field)}
                    ampm={!isTimeFormat24hr}
                    iconSize={16}
                    slotProps={{actionBar: {style: {display: "none"}}}}
                  />
                )}
              />
            </div>
            <StyledCheckboxContainer>
              <StyledCheckbox label={"Current Date & Time"} onChange={handleCurrentPOD} />
            </StyledCheckboxContainer>
          </DateTimeGroup>
          {/* Late reason */}
          <InfoTitleLeft>2. State the Reason for the Late Arrival Time</InfoTitleLeft>
          <Controller
            name="lateReason"
            control={control}
            rules={{
              required: !lateReasonDisabled(),
              validate: {
                required: (v) => {
                  if (lateReasonDisabled()) return true;
                  return v !== "(none)" || "Late reason is required";
                }
              }
            }}
            disabled={lateReasonDisabled()}
            render={({field}) => (
              <StyledSelect
                items={eventTypes}
                itemRenderer={eventTypeItemRenderer}
                filterable={false}
                onItemSelect={() => {
                  //NOOP
                }}
              >
                <StyledSelectButton
                  text={field.value && field.value.length > 1 ? field.value : "Select an option"}
                  endIcon="caret-down"
                  className={field.value && field.value.length > 1 ? "hasvalue" : ""}
                  disabled={lateReasonDisabled()}
                />
              </StyledSelect>
            )}
          />
          {/* Arrival Departure */}
          <InfoTitleLeft>
            3. State Arrival / Departure Time {!requireArrAndDepTimes && <LighterText>(optional)</LighterText>}
          </InfoTitleLeft>
          <DateTimeGroup>
            <div>
              <StyledLabel htmlFor="arrivalDate" isRequired={requireArrAndDepTimes}>
                Arrival date
              </StyledLabel>
              <Controller
                name="arrivalDateTime"
                control={control}
                rules={{
                  required: requireArrAndDepTimes,
                  validate: arrivalDateTimeValidator
                }}
                render={({field}) => (
                  <DateInput
                    isValid={!errors.arrivalDateTime}
                    formatDate={(date: Date) => {
                      return format(date, "MM/dd/yyyy");
                    }}
                    parseDate={(str: string) => {
                      return parse(str, "MM/dd/yyyy", new Date());
                    }}
                    placeholder="ex. 01/01/2021"
                    onError={(error: Date) => {
                      if (error) {
                        control.setError("arrivalDateTime", {
                          message: DateTimeErrorMsg.InvalidDate
                        });
                      }
                    }}
                    {...field}
                    onChange={(newDate: string | null) => handleDateChange(newDate, field)}
                    className="arrivalDate"
                    inputProps={{
                      id: "arrivalDate"
                    }}
                    rightElement={
                      <CalendarIcon
                        icon={light("calendar")}
                        size="sm"
                        onClick={() => {
                          const inputEl = document.querySelector("div.arrivalDate input") as HTMLInputElement;
                          inputEl?.focus();
                        }}
                      />
                    }
                  />
                )}
              />
            </div>
            <div>
              <StyledLabel htmlFor="arrivalTime" isRequired={requireArrAndDepTimes}>
                Arrival time
                <TimeInforIcon />
              </StyledLabel>
              <Controller
                name="arrivalDateTime"
                control={control}
                rules={{
                  required: requireArrAndDepTimes,
                  validate: arrivalDateTimeValidator
                }}
                render={({field}) => (
                  <TimePicker
                    isValid={!errors.arrivalDateTime}
                    className="optimization-time-picker arrivalTime"
                    {...field}
                    value={field.value ? new Date(field.value) : undefined}
                    onChange={(value: unknown) => handleTimeChange(value, field)}
                    ampm={!isTimeFormat24hr}
                    iconSize={16}
                    slotProps={{actionBar: {style: {display: "none"}}}}
                  />
                )}
              />
            </div>
            <StyledCheckboxContainer>
              <StyledCheckbox label={"Current Date & Time"} onChange={handleCurrentArrival} />
            </StyledCheckboxContainer>
          </DateTimeGroup>
          <DateTimeGroup>
            <div>
              <StyledLabel htmlFor="departureDate" isRequired={requireArrAndDepTimes}>
                Departure date
              </StyledLabel>
              <Controller
                name="departureDateTime"
                control={control}
                rules={{
                  required: requireArrAndDepTimes,
                  validate: departureDateTimeValidator
                }}
                render={({field}) => (
                  <DateInput
                    isValid={!errors.departureDateTime}
                    formatDate={(date: Date) => {
                      return format(date, "MM/dd/yyyy");
                    }}
                    parseDate={(str: string) => {
                      return parse(str, "MM/dd/yyyy", new Date());
                    }}
                    placeholder="ex. 01/01/2021"
                    onError={(error: Date) => {
                      if (error) {
                        control.setError("departureDateTime", {
                          message: DateTimeErrorMsg.InvalidDate
                        });
                      }
                    }}
                    {...field}
                    onChange={(newDate: string | null) => handleDateChange(newDate, field)}
                    inputProps={{
                      id: "departureDate"
                    }}
                    className="departureDate"
                    rightElement={
                      <CalendarIcon
                        icon={light("calendar")}
                        size="sm"
                        onClick={() => {
                          const inputEl = document.querySelector("div.departureDate input") as HTMLInputElement;
                          inputEl?.focus();
                        }}
                      />
                    }
                  />
                )}
              />
            </div>
            <div>
              <StyledLabel htmlFor="departureTime" isRequired={requireArrAndDepTimes}>
                Departure time
                <TimeInforIcon />
              </StyledLabel>
              <Controller
                name="departureDateTime"
                control={control}
                rules={{
                  required: requireArrAndDepTimes,
                  validate: departureDateTimeValidator
                }}
                render={({field}) => (
                  <TimePicker
                    isValid={!errors.departureDateTime}
                    className="optimization-time-picker departureTime"
                    {...field}
                    value={field.value ? new Date(field.value) : undefined}
                    onChange={(value: unknown) => handleTimeChange(value, field)}
                    ampm={!isTimeFormat24hr}
                    iconSize={16}
                    slotProps={{actionBar: {style: {display: "none"}}}}
                  />
                )}
              />
            </div>
            <StyledCheckboxContainer>
              <StyledCheckbox label={"Current Date & Time"} onChange={handleCurrentDeparture} />
            </StyledCheckboxContainer>
          </DateTimeGroup>
          {/* Optional Peices and Weight */}
          <InfoTitleLeft>
            4. Pieces / Weight <LighterText>(optional)</LighterText>
          </InfoTitleLeft>
          <PiecesAndWeightGroup>
            <div>
              <StyledLabel htmlFor="pieces">Pieces</StyledLabel>
              <Controller
                name="pieces"
                control={control}
                disabled={piecesOnTabExists}
                render={({field}) => <StyledInputGroup placeholder="0" {...field} />}
              ></Controller>
            </div>
            <div>
              <StyledLabel htmlFor="weight">Weight</StyledLabel>
              <Controller
                name="weight"
                control={control}
                disabled={piecesOnTabExists}
                render={({field}) => (
                  <StyledInputGroup placeholder="0" {...field} rightElement={<WeightLbsTag>lbs</WeightLbsTag>} />
                )}
              ></Controller>
            </div>
          </PiecesAndWeightGroup>
          <ButtonGroup>
            <CancelButton
              variant="minimal"
              text="Cancel"
              onClick={() => {
                onCancel?.();
              }}
            />
            <MarkCompleteButton type="submit" text="Mark Complete" disabled={!isValid || !isEmpty(errors)} />
          </ButtonGroup>
          {[...new Set(Object.values(errors).map((err) => err.message))].map((item) => (
            <StyledErrorText data-testid="error-message" key={item}>
              {item}
            </StyledErrorText>
          ))}
        </StyledForm>
      </DialogBody>
    </StyledDialog>
  );
};

const StyledErrorText = styled(Text)`
  font-family: Roboto;
  font-size: smaller;
  color: rgba(250, 84, 94, 1);
  margin: 10px 0;
`;

const StyledSelectButton = styled(Button)`
  color: rgba(141, 164, 190, 1) !important;
  background-color: white !important;
  width: 216px;

  &.hasvalue,
  &.hasvalue:hover {
    background-color: rgba(141, 164, 190, 1) !important;
    color: white !important;
  }

  &.hasvalue .bp5-icon,
  &.hasvalue .bp5-icon:hover {
    background-color: rgba(141, 164, 190, 1) !important;
    color: white !important;
  }

  :disabled {
    color: rgba(95, 107, 124, 0.6) !important;
  }

  .bp5-button-text {
    flex: 1;
  }

  .bp5-icon {
    color: rgba(141, 164, 190, 1) !important;
  }
`;
const StyledSelect = styled(Select<EventType>)`
  width: 216px;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
`;

const CalendarIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
  margin-top: 8px;
  margin-right: 8px;
`;

const StyledInputGroup = styled(InputGroup)`
  width: 216px;
`;

const WeightLbsTag = styled.div`
  margin-top: 0.5em;
  margin-right: 0.5em;
`;

const LighterText = styled.span`
  color: rgba(121, 121, 121, 1);
`;

const StyledLabel = styled(Label)<{isRequired?: boolean}>`
  color: rgba(121, 121, 121, 1);
  font-size: 12px;
  height: 16px;
  margin-bottom: 4px !important;
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;

  :after {
    content: " *";
    color: red;
    display: ${(props) => (props.isRequired ? "inline-block" : "none")};
  }
`;

const DateTimeGroup = styled.div`
  display: grid;
  grid-template-columns: 135px 120px auto;
  align-items: center;
  gap: 12px;
  margin: 12px 0;
`;

const PiecesAndWeightGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 12px;
  margin-bottom: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 48px;
`;

const MarkCompleteButton = styled(Button)`
  &.bp5-button {
    color: white;
    background: linear-gradient(180deg, #214a89 0%, #14305a 100%);
  }

  &.bp5-button:disabled {
    color: rgba(95, 107, 124, 0.6);
    background: rgba(211, 216, 222, 0.5);
  }
`;

const CancelButton = styled(Button)`
  color: rgba(20, 48, 90, 1) !important;
`;

const DialogBody = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 12px 24px;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const InfoTitleLeft = styled.h3`
  font-weight: normal;
`;

const InfoTitleRight = styled.h3`
  font-weight: normal;
  text-align: right;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoLineItemRight = styled.div`
  text-align: right;
`;

const InfoLineItemLeft = styled.div`
  text-align: left;
`;

const HeaderHR = styled.hr`
  height: 5px;
  background-color: rgba(20, 48, 90, 1);
  width: 100%;
  border-radius: 5px;
`;

const ManifestSequence = styled.div`
  text-align: center;
  font-size: 18px;
  gap: 6px;
`;

const StyledDialog = styled(Dialog)`
  .bp5-dialog-header {
    background-color: rgba(248, 248, 248, 1);
  }

  .bp5-heading {
    font-size: smaller;
    text-transform: uppercase;
  }
`;

const StyledForm = styled.form``;

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 8px;
`;

const StyledCheckboxContainer = styled.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
`;

export default CompleteStopDialog;
export {TimeType, TriggerType};
export type {CompleteStopDialogProps};
